export type AdPlacement = "rewarded-hint" | "level-transition";

export type AdStatus = "idle" | "consent-requested" | "ready" | "showing" | "closed" | "failed";

export type ConsentStatus = "unknown" | "not-required" | "required" | "obtained";

export interface AdStatusEvent {
  readonly placement: AdPlacement;
  readonly status: AdStatus;
  readonly adUnitAlias?: string;
  readonly errorCode?: string;
}

export interface RewardedAdResult {
  readonly placement: "rewarded-hint";
  readonly completed: boolean;
  readonly rewardGranted: boolean;
  readonly errorCode?: string;
}

export interface InterstitialAdResult {
  readonly placement: "level-transition";
  readonly completed: boolean;
  readonly errorCode?: string;
}

export interface AdService {
  canShowRewardedHint(): boolean;
  showRewardedHint(): Promise<RewardedAdResult>;
  canShowInterstitial(): boolean;
  showInterstitial(): Promise<InterstitialAdResult>;
  requestConsent(): Promise<ConsentStatus>;
  isConsentRequired(): boolean;
  showPrivacyOptionsForm(): Promise<ConsentStatus>;
  onStatusChange(listener: (event: AdStatusEvent) => void): () => void;
}

export interface MockAdServiceOptions {
  readonly rewardedAvailable?: boolean;
  readonly rewardedGrantsReward?: boolean;
  readonly interstitialAvailable?: boolean;
  readonly interstitialCompletes?: boolean;
  readonly consentStatus?: ConsentStatus;
}

export class MockAdService implements AdService {
  private readonly listeners = new Set<(event: AdStatusEvent) => void>();
  private consentStatus: ConsentStatus;

  public constructor(private readonly options: MockAdServiceOptions = {}) {
    this.consentStatus = options.consentStatus ?? "not-required";
  }

  public canShowRewardedHint(): boolean {
    return this.options.rewardedAvailable ?? true;
  }

  public async showRewardedHint(): Promise<RewardedAdResult> {
    if (!this.canShowRewardedHint()) {
      this.emit({ placement: "rewarded-hint", status: "failed", errorCode: "MOCK_NOT_READY" });
      return {
        placement: "rewarded-hint",
        completed: false,
        rewardGranted: false,
        errorCode: "MOCK_NOT_READY"
      };
    }

    this.emit({ placement: "rewarded-hint", status: "showing" });
    const rewardGranted = this.options.rewardedGrantsReward ?? true;
    this.emit({ placement: "rewarded-hint", status: "closed" });
    return {
      placement: "rewarded-hint",
      completed: rewardGranted,
      rewardGranted
    };
  }

  public canShowInterstitial(): boolean {
    return this.options.interstitialAvailable ?? true;
  }

  public async showInterstitial(): Promise<InterstitialAdResult> {
    if (!this.canShowInterstitial()) {
      this.emit({
        placement: "level-transition",
        status: "failed",
        errorCode: "MOCK_INTERSTITIAL_NOT_READY"
      });
      return {
        placement: "level-transition",
        completed: false,
        errorCode: "MOCK_INTERSTITIAL_NOT_READY"
      };
    }

    this.emit({ placement: "level-transition", status: "showing" });
    const completed = this.options.interstitialCompletes ?? true;
    this.emit({ placement: "level-transition", status: "closed" });
    return {
      placement: "level-transition",
      completed
    };
  }

  public async requestConsent(): Promise<ConsentStatus> {
    this.emit({ placement: "level-transition", status: "consent-requested" });
    if (this.consentStatus === "required") {
      this.consentStatus = "obtained";
    }
    return this.consentStatus;
  }

  public isConsentRequired(): boolean {
    return this.consentStatus === "required";
  }

  public async showPrivacyOptionsForm(): Promise<ConsentStatus> {
    if (this.consentStatus === "unknown") {
      this.consentStatus = "not-required";
    }
    return this.consentStatus;
  }

  public onStatusChange(listener: (event: AdStatusEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(event: AdStatusEvent): void {
    for (const listener of this.listeners) {
      listener(event);
    }
  }
}

export interface InterstitialFrequencyGateOptions {
  readonly firstEligibleLevelOrder?: number;
  readonly levelsBetweenInterstitials?: number;
  readonly cooldownMs?: number;
}

export class InterstitialFrequencyGate {
  private lastShownAtMs: number | null = null;
  private lastShownAfterLevelOrder: number | null = null;

  public constructor(private readonly options: InterstitialFrequencyGateOptions = {}) {}

  public canShowAfterLevel(levelOrder: number, nowMs: number): boolean {
    if (levelOrder < this.firstEligibleLevelOrder) {
      return false;
    }
    if (this.lastShownAtMs !== null && nowMs - this.lastShownAtMs < this.cooldownMs) {
      return false;
    }
    if (
      this.lastShownAfterLevelOrder !== null &&
      levelOrder - this.lastShownAfterLevelOrder < this.levelsBetweenInterstitials
    ) {
      return false;
    }
    return (levelOrder - this.firstEligibleLevelOrder) % this.levelsBetweenInterstitials === 0;
  }

  public markShown(levelOrder: number, nowMs: number): void {
    this.lastShownAfterLevelOrder = levelOrder;
    this.lastShownAtMs = nowMs;
  }

  private get firstEligibleLevelOrder(): number {
    return this.options.firstEligibleLevelOrder ?? 6;
  }

  private get levelsBetweenInterstitials(): number {
    return this.options.levelsBetweenInterstitials ?? 4;
  }

  private get cooldownMs(): number {
    return this.options.cooldownMs ?? 90_000;
  }
}

export function createMockAdService(options?: MockAdServiceOptions): AdService {
  return new MockAdService(options);
}
